import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Ticket, Plus, Send, ListTodo, XCircle, ArrowLeft, Hash, Building2, Clock, Loader2, Paperclip, Smile, Sparkles, FileText, ExternalLink, X, Hourglass, Mail, MessageCircle, Globe, PenLine, List, LayoutGrid, Lock, User, Flag, Inbox, UserCheck, AlertTriangle, History, ChevronRight, StickyNote, BookOpen, Search, CheckSquare, Square, Trash2 } from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { api, fileUrl, formatApiError } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { useRealtime } from "../context/RealtimeContext";
import { useAuth } from "../context/AuthContext";
import { Avatar } from "../components/Avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const SOURCE = { email: "Email", chat: "Chat", widget: "Widget", manual: "Manuel" };
const SOURCE_ICON = { email: Mail, chat: MessageCircle, widget: Globe, manual: PenLine };
const SOURCE_COLOR = { email: "#3B82F6", chat: "#10B981", widget: "#A78BFA", manual: "#F59E0B" };
function SourceTag({ source, showLabel = true }) {
  const Icon = SOURCE_ICON[source] || PenLine;
  const c = SOURCE_COLOR[source] || "#71717a";
  return (
    <span data-testid={`source-tag-${source}`} className="inline-flex items-center gap-1 text-[11px] font-medium" style={{ color: c }} title={`Canal : ${SOURCE[source] || source}`}>
      <Icon size={12} />{showLabel && (SOURCE[source] || source)}
    </span>
  );
}
const TSTATUS = {
  open: { l: "Ouvert", c: "#10B981" }, answered: { l: "Répondu", c: "#F59E0B" },
  in_progress: { l: "En cours", c: "#8B5CF6" },
  converted: { l: "Converti", c: "#3B82F6" }, closed: { l: "Fermé", c: "#E60000" },
  auto_closed: { l: "Fermé auto", c: "#B91C1C" },
};
const PRIORITY = {
  low: { l: "Basse", c: "#71717a" }, medium: { l: "Normale", c: "#3B82F6" },
  high: { l: "Haute", c: "#F59E0B" }, urgent: { l: "Urgente", c: "#FF3B3B" },
};
const TL_COLOR = {
  created: "#3B82F6", ack_sent: "#10B981", assigned: "#8B5CF6", unassigned: "#71717a",
  status_changed: "#F59E0B", priority_changed: "#F59E0B", first_response: "#10B981",
  agent_reply: "#3B82F6", client_reply: "#2563EB", internal_note: "#A1A1AA",
  converted: "#10B981", closed: "#71717a",
};

const EMOJIS = ["👍", "🙏", "✅", "🎉", "😊", "🙂", "👋", "💡", "🚀", "🔥", "⭐", "❤️", "😅", "🤔", "👀", "📎", "📅", "⏰", "✍️", "🤝"];

const RESOLVED = ["converted", "closed", "auto_closed"];
function firstAgentReplyAt(t) {
  const r = (t.replies || []).filter((x) => x.is_agent && !x.internal).map((x) => x.created_at).sort();
  return r.length ? r[0] : (t.last_agent_reply_at || null);
}
function pickupSeconds(t, now) {
  const f = firstAgentReplyAt(t);
  const end = f ? new Date(f).getTime() : (RESOLVED.includes(t.status) ? new Date(t.updated_at).getTime() : now);
  return Math.max(0, (end - new Date(t.created_at).getTime()) / 1000);
}
function resolutionSeconds(t, now) {
  const end = RESOLVED.includes(t.status) ? new Date(t.updated_at).getTime() : now;
  return Math.max(0, (end - new Date(t.created_at).getTime()) / 1000);
}
function isPickedUp(t) { return !!firstAgentReplyAt(t) || RESOLVED.includes(t.status); }
function fmtWait(s) {
  s = Math.floor(s);
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  if (d > 0) return `${d}j ${h}h`;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${m}m ${String(sec).padStart(2, "0")}s`;
  return `${sec}s`;
}
function fmtDate(iso) { return iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "—"; }
function fmtTime(iso) { return iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""; }

const REPLY_TEMPLATES = [
  { label: "Accusé de réception", body: "Bonjour {client},\n\nNous avons bien reçu votre demande et nous la traitons dans les meilleurs délais. Nous revenons vers vous très rapidement.\n\nBien cordialement,\n{moi} — {entreprise}" },
  { label: "Demande d'informations", body: "Bonjour {client},\n\nAfin de traiter votre demande au mieux, pourriez-vous nous transmettre les éléments suivants :\n- \n- \n\nMerci d'avance !\n{moi} — {entreprise}" },
  { label: "Demande en cours", body: "Bonjour {client},\n\nVotre demande est en cours de traitement par notre équipe. Nous vous tiendrons informé(e) de son avancement.\n\nBien cordialement,\n{moi} — {entreprise}" },
  { label: "Résolution", body: "Bonjour {client},\n\nNous avons le plaisir de vous confirmer que votre demande a été traitée. N'hésitez pas à revenir vers nous si besoin.\n\nBien cordialement,\n{moi} — {entreprise}" },
];

function TStatus({ s }) {
  const c = TSTATUS[s] || TSTATUS.open;
  return <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[11px] font-mono-label uppercase tracking-wider border" style={{ color: c.c, borderColor: c.c + "40", background: c.c + "12" }}><span className="w-1.5 h-1.5 rounded-full" style={{ background: c.c }} />{c.l}</span>;
}
function PriorityDot({ p, size = 8 }) {
  const c = PRIORITY[p] || PRIORITY.medium;
  return <span title={`Priorité : ${c.l}`} className="rounded-full shrink-0 inline-block" style={{ background: c.c, width: size, height: size }} />;
}

const VIEWS = [
  { k: "all", l: "Tous", icon: Inbox },
  { k: "unassigned", l: "Non assignés", icon: User },
  { k: "mine", l: "Mes tickets", icon: UserCheck },
  { k: "priority", l: "Prioritaires", icon: AlertTriangle },
];

export default function TicketsPage() {
  const { managed, selectedId } = useCompany();
  const { events } = useRealtime();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    const pf = location.state?.prefillClient;
    if (pf) {
      setForm((f) => ({ ...f, client_email: pf.email || "", company_id: f.company_id || (selectedId && selectedId !== "all" ? selectedId : "") }));
      setCreating(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);
  const replyFileRef = useRef();
  const [tickets, setTickets] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState("");
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [uploadingReply, setUploadingReply] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showKb, setShowKb] = useState(false);
  const [kbArticles, setKbArticles] = useState([]);
  const [kbQuery, setKbQuery] = useState("");
  const [composerMode, setComposerMode] = useState("reply");
  const [creating, setCreating] = useState(false);
  const [convert, setConvert] = useState(null);
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({ company_id: "", subject: "", body: "", client_email: "", source: "manual", priority: "medium" });
  const [filter, setFilter] = useState("all");
  const [scope, setScope] = useState("all");
  const [quickTitle, setQuickTitle] = useState("");
  const [members2, setMembers2] = useState([]);
  const [selIds, setSelIds] = useState(new Set());
  const toggleSel = (id) => setSelIds((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearSel = () => setSelIds(new Set());
  const [view, setView] = useState(user?.prefs?.tickets_view || "list");
  const kanbanReturn = useRef(false);
  const changeView = (v) => { setView(v); api.patch("/profile", { prefs: { ...(user?.prefs || {}), tickets_view: v } }).then(({ data }) => setUser(data)).catch(() => {}); };
  const openFromKanban = (t) => { kanbanReturn.current = true; setView("list"); openTicket(t); };
  const backToList = () => { setActive(null); if (kanbanReturn.current) { kanbanReturn.current = false; setView("kanban"); } };
  const moveTicketStatus = async (id, status) => {
    setTickets((prev) => prev.map((t) => (t.id === id ? { ...t, status } : t)));
    try { await api.patch(`/tickets/${id}`, { status }); } catch { toast.error("Échec du déplacement"); load(); }
  };
  const patchTicketInline = async (id, updates) => {
    setTickets((prev) => prev.map((t) => (t.id === id ? { ...t, ...updates } : t)));
    try { const { data } = await api.patch(`/tickets/${id}`, updates); setTickets((prev) => prev.map((t) => (t.id === id ? { ...t, ...data } : t))); }
    catch { toast.error("Échec de la mise à jour"); load(); }
  };
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const i = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(i); }, []);
  const [ticketContacts, setTicketContacts] = useState([]);
  const [selClient, setSelClient] = useState(null);
  useEffect(() => {
    if (!creating || !form.company_id) { setTicketContacts([]); return; }
    api.get(`/contacts?company_id=${form.company_id}`).then(({ data }) => setTicketContacts(data)).catch(() => setTicketContacts([]));
  }, [creating, form.company_id]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = selectedId && selectedId !== "all" ? `/tickets?company_id=${selectedId}` : "/tickets";
      const { data } = await api.get(url);
      setTickets(data);
    } finally { setLoading(false); }
  }, [selectedId]);

  useEffect(() => { load(); }, [load, events]);
  useEffect(() => { api.get("/users").then((r) => setMembers2(r.data.map((u) => ({ user_id: u.id, name: u.name })))).catch(() => {}); }, []);
  useEffect(() => { clearSel(); }, [scope, filter, selectedId]);

  const quickAddTicket = async () => {
    if (!quickTitle.trim()) return;
    const cid = selectedId && selectedId !== "all" ? selectedId : (managed[0]?.id);
    if (!cid) { toast.error("Sélectionnez une entreprise"); return; }
    try { await api.post("/tickets", { company_id: cid, subject: quickTitle, body: "", source: "manual", priority: "medium" }); setQuickTitle(""); load(); toast.success("Ticket créé"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const bulkPatch = async (patch) => {
    const ids = [...selIds];
    try { await api.post("/tickets/bulk", { ids, patch }); toast.success(`${ids.length} ticket(s) mis à jour`); clearSel(); load(); }
    catch { toast.error("Action en lot impossible"); }
  };
  const bulkDelete = async () => {
    const ids = [...selIds];
    try { await api.post("/tickets/bulk", { ids, delete: true }); toast.success(`${ids.length} ticket(s) supprimé(s)`); clearSel(); load(); }
    catch { toast.error("Suppression impossible"); }
  };

  const openTicket = async (t) => {
    setComposerMode("reply");
    const { data } = await api.get(`/tickets/${t.id}`);
    setActive(data);
    const cid = data.company_id;
    api.get(`/companies/${cid}/members`).then((r) => setMembers(r.data.filter((m) => ["member", "agent", "owner"].includes(m.role)))).catch(() => setMembers([]));
  };

  const patchActive = async (patch) => {
    const prev = active;
    setActive((a) => ({ ...a, ...patch }));
    try {
      const { data } = await api.patch(`/tickets/${active.id}`, patch);
      setActive(data);
      setTickets((arr) => arr.map((x) => (x.id === data.id ? { ...x, ...data } : x)));
    } catch { setActive(prev); toast.error("Mise à jour impossible"); }
  };

  const sendReply = async () => {
    if (!replyText.trim() && replyAttachments.length === 0) return;
    const internal = composerMode === "note";
    const { data } = await api.post(`/tickets/${active.id}/reply`, { body: replyText, attachments: replyAttachments, internal });
    setActive(data); setReplyText(""); setReplyAttachments([]); load();
    toast.success(internal ? "Note interne ajoutée" : "Réponse envoyée");
  };

  const insertEmoji = (e) => { setReplyText((t) => t + e); setShowEmoji(false); };

  const openKb = async () => {
    setShowKb((s) => !s); setShowTemplates(false); setShowEmoji(false);
    if (!showKb && active?.company_id) {
      try { const { data } = await api.get(`/kb/articles?company_id=${active.company_id}`); setKbArticles(data); } catch { setKbArticles([]); }
    }
  };
  const insertArticle = (a) => {
    setReplyText((t) => `${t ? t + "\n\n" : ""}📄 ${a.title}\n\n${a.content}\n`);
    setShowKb(false); setKbQuery("");
    toast.success("Article inséré dans la réponse");
  };

  const applyTemplate = (tpl) => {
    const filled = tpl.body
      .replaceAll("{client}", active?.client_name || active?.client_email || "")
      .replaceAll("{entreprise}", active?.company_name || "")
      .replaceAll("{moi}", user?.name || "");
    setReplyText(filled);
    setShowTemplates(false);
  };

  const uploadReplyFile = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploadingReply(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      setReplyAttachments((a) => [...a, data]);
      toast.success("Pièce jointe ajoutée");
    } catch { toast.error("Échec de l'envoi"); } finally { setUploadingReply(false); e.target.value = ""; }
  };

  const createTicket = async () => {
    const cid = form.company_id || (managed[0]?.id);
    if (!cid || !form.subject) { toast.error("Entreprise et objet requis"); return; }
    try {
      await api.post("/tickets", { ...form, company_id: cid });
      toast.success("Ticket créé"); setCreating(false);
      setForm({ company_id: "", subject: "", body: "", client_email: "", source: "manual", priority: "medium" });
      setSelClient(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doConvert = async () => {
    try {
      await api.post(`/tickets/${active.id}/convert`, convert);
      toast.success("Ticket converti en tâche", { description: "Le ticket est fermé, la tâche est créée sur le compte client" });
      setConvert(null); backToList(); load();
      navigate("/tasks");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const closeTicket = async () => { await api.post(`/tickets/${active.id}/close`); toast.success("Ticket fermé"); backToList(); load(); };

  const scopeMatch = (t) => {
    if (scope === "unassigned") return !t.assigned_to;
    if (scope === "mine") return t.assigned_to === user?.id;
    if (scope === "priority") return ["high", "urgent"].includes(t.priority);
    return true;
  };
  const visible = tickets.filter(scopeMatch);
  const filtered = filter === "all" ? visible : visible.filter((t) => t.status === filter);
  const otherTickets = active ? tickets.filter((t) => t.id !== active.id && t.client_email === active.client_email) : [];

  return (
    <div className="flex flex-col h-full fd-clean">
      <div className="h-12 border-b border-white/10 bg-card flex items-center justify-between px-4 shrink-0">
        <div className="flex items-center gap-2"><Ticket size={16} className="text-klein" /><span className="font-cabinet font-bold text-base">Tickets</span><span className="text-xs text-zinc-500 hidden sm:inline">· {visible.length} en file</span></div>
        <div className="flex items-center gap-1 p-1 bg-white/[0.06] rounded-lg">
          <button data-testid="ticket-view-list" onClick={() => changeView("list")} className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-sm font-medium transition-colors ${view !== "kanban" ? "bg-card text-foreground shadow-sm" : "text-zinc-500 hover:text-zinc-300"}`}><List size={15} /> Liste</button>
          <button data-testid="ticket-view-kanban" onClick={() => changeView("kanban")} className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-sm font-medium transition-colors ${view === "kanban" ? "bg-card text-foreground shadow-sm" : "text-zinc-500 hover:text-zinc-300"}`}><LayoutGrid size={15} /> Kanban</button>
        </div>
      </div>
      {view === "kanban" ? (
        <TicketKanban tickets={visible} onOpen={openFromKanban} onMove={moveTicketStatus} now={now} />
      ) : (
      <div className="flex flex-1 min-h-0">
      {/* === LEFT: triage list === */}
      <div className={`${active ? "hidden" : "flex"} flex-col w-full bg-card shrink-0 min-h-0`}>
        <div className="h-14 flex items-center justify-between px-4 border-b border-white/10 shrink-0">
          <span className="font-cabinet font-bold text-lg">File de tickets</span>
          <button data-testid="new-ticket-button" onClick={() => setCreating(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-klein text-zinc-950 text-sm font-semibold hover:brightness-110"><Plus size={15} /> Ticket</button>
        </div>
        {/* Quick add */}
        <div className="px-3 py-2 border-b border-white/10 flex items-center gap-2">
          <Plus size={15} className="text-zinc-400 shrink-0" />
          <input data-testid="ticket-quick-add-input" value={quickTitle} onChange={(e) => setQuickTitle(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") quickAddTicket(); }} placeholder="Créer un ticket rapidement…" className="flex-1 bg-transparent text-sm focus:outline-none placeholder:text-zinc-400" />
          {quickTitle && <button data-testid="ticket-quick-add-button" onClick={quickAddTicket} className="text-xs font-medium text-klein">Créer</button>}
        </div>
        {/* Filters — scope + status on a single level */}
        <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/10 overflow-x-auto no-scrollbar" data-testid="ticket-filters">
          {VIEWS.map((v) => {
            const Icon = v.icon;
            const n = tickets.filter((t) => { if (v.k === "unassigned") return !t.assigned_to; if (v.k === "mine") return t.assigned_to === user?.id; if (v.k === "priority") return ["high", "urgent"].includes(t.priority); return true; }).length;
            const on = scope === v.k;
            return (
              <button key={v.k} data-testid={`ticket-view-scope-${v.k}`} onClick={() => setScope(v.k)}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium whitespace-nowrap transition-colors shrink-0 ${on ? "bg-klein/15 text-klein" : "text-zinc-400 hover:bg-white/[0.06]"}`}>
                <Icon size={13} /> {v.l} <span className={`tabular-nums ${on ? "" : "text-zinc-500"}`}>{n}</span>
              </button>
            );
          })}
          <div className="w-px h-5 bg-white/10 mx-1 shrink-0" />
          {[["all", "Tous", "#71717a"], ...["open", "answered", "converted", "closed"].map((s) => [s, TSTATUS[s].l, TSTATUS[s].c])].map(([key, label, c]) => {
            const n = key === "all" ? visible.length : visible.filter((t) => t.status === key).length;
            const on = filter === key;
            return (
              <button key={key} data-testid={`ticket-filter-${key}`} onClick={() => setFilter(key)}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap border transition-colors shrink-0 ${on ? "" : "text-zinc-400 border-transparent hover:bg-white/[0.06]"}`}
                style={on ? { background: `${c}22`, borderColor: `${c}66`, color: c } : {}}>
                {label} <span className={`tabular-nums ${on ? "" : "text-zinc-500"}`}>{n}</span>
              </button>
            );
          })}
        </div>
        {selIds.size > 0 && (
          <div className="flex items-center gap-2 px-3 py-2 bg-klein/10 border-b border-klein/30" data-testid="ticket-bulk-bar">
            <span className="text-xs font-semibold text-klein whitespace-nowrap">{selIds.size} sélec.</span>
            <button onClick={clearSel} className="text-[11px] text-zinc-400 hover:text-foreground">Annuler</button>
            <div className="ml-auto flex items-center gap-1.5">
              <select data-testid="ticket-bulk-status" value="" onChange={(e) => e.target.value && bulkPatch({ status: e.target.value })} className="bg-background border border-white/10 rounded-md px-1.5 h-7 text-[11px] focus:outline-none">
                <option value="">Statut…</option>{["open", "answered", "converted", "closed"].map((s) => <option key={s} value={s}>{TSTATUS[s].l}</option>)}
              </select>
              <select data-testid="ticket-bulk-priority" value="" onChange={(e) => e.target.value && bulkPatch({ priority: e.target.value })} className="bg-background border border-white/10 rounded-md px-1.5 h-7 text-[11px] focus:outline-none">
                <option value="">Priorité…</option>{["urgent", "high", "medium", "low"].map((p) => <option key={p} value={p}>{PRIORITY[p].l}</option>)}
              </select>
              <select data-testid="ticket-bulk-assign" value="" onChange={(e) => bulkPatch({ assigned_to: e.target.value })} className="bg-background border border-white/10 rounded-md px-1.5 h-7 text-[11px] focus:outline-none max-w-[110px]">
                <option value="">Assigner…</option><option value="">— Personne —</option>{members2.map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
              </select>
              <button data-testid="ticket-bulk-delete" onClick={bulkDelete} title="Supprimer" className="p-1.5 rounded-md text-zinc-400 hover:text-signal hover:bg-signal/10"><Trash2 size={15} /></button>
            </div>
          </div>
        )}
        <div className="flex-1 overflow-auto">
          <div className="min-w-[860px]">
            <div className="sticky top-0 z-10 grid grid-cols-[28px_minmax(0,1fr)_160px_150px_120px_110px_150px_96px] gap-3 px-4 py-2.5 bg-card border-b border-white/10 text-[10px] font-mono-label uppercase tracking-widest text-zinc-500">
              <span></span><span>Sujet</span><span>Client</span><span>Entreprise</span><span>Statut</span><span>Priorité</span><span>Assigné</span><span>Attente</span>
            </div>
            {loading ? <div className="p-8 flex justify-center text-zinc-400"><Loader2 className="animate-spin" /></div>
              : filtered.length === 0 ? <div className="p-12 text-center text-zinc-400 text-sm">Aucun ticket dans cette vue.</div>
              : filtered.map((t) => {
                const sc = TSTATUS[t.status]?.c || "#71717a";
                const isActive = active?.id === t.id;
                const isSel = selIds.has(t.id);
                const pr = PRIORITY[t.priority] || PRIORITY.medium;
                return (
                  <div key={t.id} data-testid={`ticket-item-${t.id}`} onClick={() => openTicket(t)}
                    className={`group grid grid-cols-[28px_minmax(0,1fr)_160px_150px_120px_110px_150px_96px] gap-3 px-4 py-3 border-b border-white/[0.05] items-center cursor-pointer transition-colors ${isActive ? "bg-background" : isSel ? "bg-klein/[0.07]" : "hover:bg-white/[0.03]"}`}
                    style={{ boxShadow: `inset 3px 0 0 ${sc}` }}>
                    <button data-testid={`ticket-select-${t.id}`} onClick={(e) => { e.stopPropagation(); toggleSel(t.id); }} className={`shrink-0 transition-opacity ${isSel ? "text-klein" : "text-zinc-400 opacity-60 hover:opacity-100"}`}>
                      {isSel ? <CheckSquare size={16} /> : <Square size={16} />}
                    </button>
                    <div className="min-w-0 flex items-center gap-2">
                      <PriorityDot p={t.priority} />
                      {t.position ? <span className="inline-flex items-center gap-0.5 text-[10px] font-mono-label font-bold text-klein bg-klein/15 px-1.5 py-0.5 rounded shrink-0"><Hash size={10} />{t.position}</span> : null}
                      <span className="font-mono-label text-[10px] text-zinc-400 shrink-0 hidden lg:inline">{t.ref}</span>
                      <span className="font-medium text-sm truncate">{t.subject}</span>
                    </div>
                    <div className="text-[13px] text-zinc-300 truncate">{t.client_name || t.client_email || "—"}</div>
                    <div className="text-xs text-zinc-400 truncate flex items-center gap-1.5"><Building2 size={11} className="text-zinc-500 shrink-0" />{t.company_name}</div>
                    <div onClick={(e) => e.stopPropagation()}>
                      <select data-testid={`ticket-row-status-${t.id}`} value={t.status} onChange={(e) => patchTicketInline(t.id, { status: e.target.value })} className="fd-inline-select" style={{ color: sc, background: `${sc}1a`, borderColor: `${sc}55` }}>
                        {["open", "answered", "in_progress", "converted", "closed"].map((s) => <option key={s} value={s} style={{ color: "var(--fg)" }}>{TSTATUS[s].l}</option>)}
                      </select>
                    </div>
                    <div onClick={(e) => e.stopPropagation()}>
                      <select data-testid={`ticket-row-priority-${t.id}`} value={t.priority || "medium"} onChange={(e) => patchTicketInline(t.id, { priority: e.target.value })} className="fd-inline-select" style={{ color: pr.c, borderColor: `${pr.c}55` }}>
                        {Object.entries(PRIORITY).map(([k, v]) => <option key={k} value={k}>{v.l}</option>)}
                      </select>
                    </div>
                    <div className="min-w-0" onClick={(e) => e.stopPropagation()}>
                      <select data-testid={`ticket-row-assign-${t.id}`} value={t.assigned_to || ""} onChange={(e) => patchTicketInline(t.id, { assigned_to: e.target.value || null })} className="fd-inline-select w-full">
                        <option value="">— Non assigné</option>
                        {members2.map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
                      </select>
                    </div>
                    <div>
                      {!RESOLVED.includes(t.status)
                        ? <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono-label text-[11px] tabular-nums ${isPickedUp(t) ? "bg-emerald-500/10 text-emerald-400" : "bg-signal/10 text-signal"}`} title={isPickedUp(t) ? "En attente de résolution" : "Pas encore pris en charge"}><Hourglass size={10} /> {fmtWait(resolutionSeconds(t, now))}</span>
                        : <span className="text-[11px] text-zinc-500">Clos</span>}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      {/* === CENTER: conversation === */}
      <div className={`${active ? "flex" : "hidden"} flex-1 flex-col bg-background min-w-0`}>
        {active ? (
          <>
            <div className="h-14 border-b border-white/10 bg-card flex items-center gap-2 px-4 shrink-0">
              <button onClick={backToList} className="flex p-1.5 rounded-md hover:bg-white/[0.06]" title="Retour à la liste"><ArrowLeft size={18} /></button>
              <PriorityDot p={active.priority} size={9} />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{active.subject}</div>
                <div className="text-[11px] text-zinc-400 font-mono-label flex items-center gap-1.5">{active.ref} · {active.company_name} <span className="text-zinc-600">·</span> <SourceTag source={active.source} /></div>
              </div>
              {["open", "answered"].includes(active.status) && <>
                <button data-testid="convert-ticket-button" onClick={() => setConvert({ title: active.subject, description: active.body, assigned_to: active.assigned_to || "", client_budget: 0, internal_budget: 0, member_budget: 0, deadline: "" })} className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-klein text-zinc-950 text-sm font-medium hover:brightness-110"><ListTodo size={15} /> Convertir</button>
                <button data-testid="close-ticket-button" onClick={closeTicket} title="Fermer le ticket" className="p-2 rounded-md hover:bg-signal/10 text-zinc-400 hover:text-signal"><XCircle size={18} /></button>
              </>}
            </div>
            <div className="flex-1 overflow-y-auto p-4 lg:p-6 space-y-4">
              {active.status === "auto_closed" && <div className="flex items-center gap-2 text-signal text-sm bg-red-500/10 border border-red-500/30 rounded-md p-3"><Clock size={16} /> Fermé automatiquement : pas de réponse du client sous 10 jours.</div>}
              {/* original message */}
              <div className="bg-card border border-white/10 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2"><Avatar name={active.client_name} color="#27272A" size={32} /><div className="min-w-0"><div className="font-semibold text-sm truncate">{active.client_name}</div><div className="text-[11px] text-zinc-400 truncate">{active.client_email} · {fmtTime(active.created_at)}</div></div></div>
                <p className="text-sm text-zinc-300 whitespace-pre-wrap">{active.body}</p>
              </div>
              {/* thread */}
              {(active.replies || []).map((r, i) => (
                r.internal ? (
                  <div key={i} data-testid={`ticket-note-${i}`} className="bg-amber-500/[0.08] border border-amber-500/30 rounded-lg p-3 ml-4">
                    <div className="flex items-center gap-1.5 text-[11px] mb-1 font-mono-label text-amber-400"><Lock size={11} /> Note interne · {r.author_name} · {fmtTime(r.created_at)}</div>
                    <p className="text-sm whitespace-pre-wrap text-amber-100/90">{r.body}</p>
                  </div>
                ) : (
                <div key={i} className={`max-w-[85%] p-3 rounded-2xl border ${r.is_agent ? "bg-klein/10 border-klein/25 ml-auto rounded-tr-sm" : "bg-card border-white/10 rounded-tl-sm"}`}>
                  <div className={`text-[11px] mb-1 font-mono-label ${r.is_agent ? "text-klein" : "text-zinc-400"}`}>{r.author_name} · {r.is_agent ? "Agent" : "Client"} · {fmtTime(r.created_at)}</div>
                  {r.body && <p className="text-sm whitespace-pre-wrap text-foreground">{r.body}</p>}
                  {(r.attachments || []).length > 0 && (
                    <div className="mt-2 space-y-1.5">
                      {r.attachments.map((a, j) => (
                        <a key={j} href={fileUrl(a.id)} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-background border border-white/10 hover:border-white/30">
                          <FileText size={14} className="shrink-0" /><span className="truncate flex-1">{a.name}</span><ExternalLink size={12} className="shrink-0 opacity-60" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
                )
              ))}
            </div>
            {(
              <div className="p-3 bg-card border-t border-white/10 shrink-0">
                {RESOLVED.includes(active.status) && (
                  <div data-testid="ticket-reopen-notice" className="mb-2 flex items-center gap-2 text-[11px] text-amber-400 bg-amber-500/10 border border-amber-500/25 rounded-md px-2.5 py-1.5">
                    <History size={12} /> Ce ticket est {TSTATUS[active.status]?.l?.toLowerCase()}. Répondre le rouvrira.
                  </div>
                )}
                {/* composer mode tabs */}
                <div className="flex items-center gap-1 mb-2">
                  <button data-testid="composer-mode-reply" onClick={() => setComposerMode("reply")} className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium transition-colors ${composerMode === "reply" ? "bg-klein/15 text-klein" : "text-zinc-400 hover:bg-white/[0.06]"}`}><Send size={13} /> Répondre au client</button>
                  <button data-testid="composer-mode-note" onClick={() => setComposerMode("note")} className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium transition-colors ${composerMode === "note" ? "bg-amber-500/15 text-amber-400" : "text-zinc-400 hover:bg-white/[0.06]"}`}><StickyNote size={13} /> Note interne</button>
                </div>
                <div className={`rounded-2xl border bg-background overflow-hidden transition-all ${composerMode === "note" ? "border-amber-500/40 focus-within:border-amber-500/60 bg-amber-500/[0.04]" : "border-white/10 focus-within:border-klein/50 focus-within:ring-1 focus-within:ring-klein/30"}`}>
                  {replyAttachments.length > 0 && (
                    <div className="flex flex-wrap gap-2 p-3 pb-0">
                      {replyAttachments.map((a, i) => (
                        <span key={i} data-testid={`reply-attachment-chip-${i}`} className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-lg bg-klein/10 border border-klein/30 text-klein text-xs font-medium">
                          <FileText size={12} /><span className="max-w-[140px] truncate">{a.name}</span>
                          <button onClick={() => setReplyAttachments((arr) => arr.filter((_, j) => j !== i))} className="hover:bg-klein/20 rounded p-0.5"><X size={12} /></button>
                        </span>
                      ))}
                    </div>
                  )}
                  <textarea data-testid="ticket-reply-input" rows={6} value={replyText} onChange={(e) => setReplyText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); sendReply(); } }} placeholder={composerMode === "note" ? "Note visible uniquement par l'équipe…" : "Répondre au client…  (Ctrl+Entrée pour envoyer)"} className="w-full bg-transparent border-0 resize-y px-4 py-3 min-h-[130px] max-h-[400px] text-sm leading-relaxed text-foreground placeholder:text-zinc-500 focus:outline-none focus:ring-0" />
                  <div className="flex items-center justify-between px-3 py-2 border-t border-white/5 bg-white/[0.03]">
                    <div className="flex items-center gap-1 relative">
                      <button data-testid="reply-templates-button" onClick={() => { setShowTemplates((s) => !s); setShowEmoji(false); }} title="Modèles de réponse" className="p-2 rounded-lg text-zinc-400 hover:bg-white/10 hover:text-foreground transition-colors"><Sparkles size={18} /></button>
                      {showTemplates && (
                        <>
                          <div className="fixed inset-0 z-30" onClick={() => setShowTemplates(false)} />
                          <div data-testid="reply-templates-menu" className="absolute bottom-full left-0 mb-2 z-40 w-72 max-w-[calc(100vw-1.5rem)] bg-card border border-white/10 rounded-xl shadow-2xl p-1.5 animate-in fade-in">
                            <p className="px-2.5 py-1.5 text-[10px] uppercase tracking-widest text-zinc-500">Modèles de réponse</p>
                            {REPLY_TEMPLATES.map((tpl, i) => (
                              <button key={i} data-testid={`reply-template-${i}`} onClick={() => applyTemplate(tpl)} className="w-full text-left px-2.5 py-2 rounded-lg hover:bg-white/[0.06] text-sm font-medium">{tpl.label}</button>
                            ))}
                          </div>
                        </>
                      )}
                      <button data-testid="reply-emoji-button" onClick={() => { setShowEmoji((s) => !s); setShowTemplates(false); }} title="Emoji" className="p-2 rounded-lg text-zinc-400 hover:bg-white/10 hover:text-foreground transition-colors"><Smile size={18} /></button>
                      {showEmoji && (
                        <>
                          <div className="fixed inset-0 z-30" onClick={() => setShowEmoji(false)} />
                          <div data-testid="reply-emoji-menu" className="absolute bottom-full left-0 mb-2 z-40 w-64 max-w-[calc(100vw-1.5rem)] bg-card border border-white/10 rounded-xl shadow-2xl p-2 grid grid-cols-7 gap-1 animate-in fade-in">
                            {EMOJIS.map((e) => (
                              <button key={e} data-testid={`reply-emoji-${e}`} onClick={() => insertEmoji(e)} className="text-xl p-1 rounded-md hover:bg-white/10 transition-colors">{e}</button>
                            ))}
                          </div>
                        </>
                      )}
                      <button data-testid="reply-attach-button" onClick={() => replyFileRef.current.click()} disabled={uploadingReply} title="Joindre un fichier" className="p-2 rounded-lg text-zinc-400 hover:bg-white/10 hover:text-foreground transition-colors">{uploadingReply ? <Loader2 size={18} className="animate-spin" /> : <Paperclip size={18} />}</button>
                      <input ref={replyFileRef} type="file" className="hidden" onChange={uploadReplyFile} data-testid="reply-file-input" />
                      <button data-testid="reply-kb-button" onClick={openKb} title="Partager un article de la base de connaissances" className="p-2 rounded-lg text-zinc-400 hover:bg-white/10 hover:text-foreground transition-colors"><BookOpen size={18} /></button>
                      {showKb && (
                        <>
                          <div className="fixed inset-0 z-30" onClick={() => setShowKb(false)} />
                          <div data-testid="reply-kb-menu" className="absolute bottom-full left-0 mb-2 z-40 w-80 max-w-[calc(100vw-1.5rem)] bg-card border border-white/10 rounded-xl shadow-2xl p-2 animate-in fade-in">
                            <p className="px-1.5 py-1 text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5"><BookOpen size={11} /> Base de connaissances</p>
                            <div className="flex items-center gap-2 bg-background border border-white/10 rounded-lg px-2.5 h-8 mb-1.5">
                              <Search size={13} className="text-zinc-500" />
                              <input data-testid="reply-kb-search" autoFocus value={kbQuery} onChange={(e) => setKbQuery(e.target.value)} placeholder="Rechercher un article…" className="flex-1 bg-transparent text-xs focus:outline-none" />
                            </div>
                            <div className="max-h-64 overflow-y-auto">
                              {kbArticles.filter((a) => !kbQuery || (a.title + a.content + a.category).toLowerCase().includes(kbQuery.toLowerCase())).map((a) => (
                                <button key={a.id} data-testid={`reply-kb-article-${a.id}`} onClick={() => insertArticle(a)} className="w-full text-left px-2.5 py-2 rounded-lg hover:bg-white/[0.06]">
                                  <div className="text-sm font-medium truncate">{a.title}</div>
                                  <div className="text-[11px] text-zinc-500 truncate">{a.category} · {a.content.slice(0, 60)}…</div>
                                </button>
                              ))}
                              {kbArticles.length === 0 && <p className="px-2.5 py-3 text-xs text-zinc-500">Aucun article. Créez-en dans « Base de connaissances ».</p>}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    <button data-testid="send-ticket-reply" onClick={sendReply} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${composerMode === "note" ? "bg-amber-500 text-zinc-950 hover:brightness-110" : "bg-klein text-zinc-950 hover:brightness-110 hover:shadow-[0_0_15px_rgba(59, 130, 246,0.35)]"}`}>{composerMode === "note" ? <><StickyNote size={16} /> Ajouter la note</> : <><Send size={16} /> Envoyer</>}</button>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-zinc-300"><Ticket size={64} strokeWidth={1} /><p className="mt-4 text-sm text-zinc-400">Sélectionnez un ticket</p></div>
        )}
      </div>

      {/* === RIGHT: customer context === */}
      {active && (
        <aside className="hidden xl:flex flex-col w-80 border-l border-white/10 bg-card shrink-0 overflow-y-auto" data-testid="ticket-context-panel">
          <div className="p-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <Avatar name={active.client_name} color="#27272A" size={44} />
              <div className="min-w-0">
                <div className="font-cabinet font-bold text-base truncate">{active.client_name}</div>
                <div className="text-xs text-zinc-400 truncate">{active.client_email}</div>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs text-zinc-400"><Building2 size={13} className="text-zinc-500" /> {active.company_name}</div>
          </div>

          {/* SLA */}
          <div className="p-4 border-b border-white/10 grid grid-cols-2 gap-2" data-testid="ticket-sla-timers">
            <div className={`rounded-lg border p-2.5 ${isPickedUp(active) ? "bg-background border-white/10" : "bg-signal/[0.07] border-signal/30"}`} data-testid="ticket-pickup-timer">
              <div className="flex items-center gap-1 font-mono-label text-[9px] uppercase tracking-widest text-zinc-400 mb-0.5"><Clock size={10} /> Prise en charge</div>
              <div className={`font-cabinet font-bold text-base tabular-nums ${isPickedUp(active) ? "text-emerald-400" : "text-signal animate-pulse"}`}>{fmtWait(pickupSeconds(active, now))}</div>
            </div>
            <div className={`rounded-lg border p-2.5 ${RESOLVED.includes(active.status) ? "bg-background border-white/10" : "bg-amber-500/[0.07] border-amber-500/30"}`} data-testid="ticket-resolution-timer">
              <div className="flex items-center gap-1 font-mono-label text-[9px] uppercase tracking-widest text-zinc-400 mb-0.5"><Hourglass size={10} /> Attente totale</div>
              <div className={`font-cabinet font-bold text-base tabular-nums ${RESOLVED.includes(active.status) ? "text-emerald-400" : "text-amber-400"}`}>{fmtWait(resolutionSeconds(active, now))}</div>
            </div>
          </div>

          {/* Properties */}
          <div className="p-4 border-b border-white/10 space-y-3">
            <p className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-500">Propriétés</p>
            <div>
              <label className="flex items-center gap-1.5 text-[11px] text-zinc-400 mb-1"><Flag size={12} /> Priorité</label>
              <select data-testid="ticket-priority-select" value={active.priority || "medium"} onChange={(e) => patchActive({ priority: e.target.value })} className="w-full bg-background border border-white/10 rounded-lg px-2 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20">
                {Object.entries(PRIORITY).map(([k, v]) => <option key={k} value={k}>{v.l}</option>)}
              </select>
            </div>
            <div>
              <label className="flex items-center gap-1.5 text-[11px] text-zinc-400 mb-1"><UserCheck size={12} /> Assigné à</label>
              <select data-testid="ticket-assignee-select" value={active.assigned_to || ""} onChange={(e) => patchActive({ assigned_to: e.target.value })} className="w-full bg-background border border-white/10 rounded-lg px-2 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20">
                <option value="">— Non assigné —</option>
                {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name} ({m.role})</option>)}
              </select>
            </div>
            <div className="flex items-center justify-between text-xs"><span className="text-zinc-400">Statut</span><TStatus s={active.status} /></div>
            <div className="flex items-center justify-between text-xs"><span className="text-zinc-400">Canal</span><SourceTag source={active.source} /></div>
            <div className="flex items-center justify-between text-xs"><span className="text-zinc-400">Créé le</span><span className="text-zinc-300">{fmtDate(active.created_at)}</span></div>
            {active.position && ["open", "answered"].includes(active.status) && <div className="flex items-center justify-between text-xs"><span className="text-zinc-400">File</span><span className="font-bold text-klein">#{active.position}</span></div>}
          </div>

          {/* Timeline / Historique */}
          <div className="p-4 border-b border-white/10" data-testid="ticket-timeline">
            <p className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5 mb-3"><History size={12} /> Historique du ticket</p>
            {(!active.timeline || active.timeline.length === 0) ? <p className="text-xs text-zinc-500">Aucun événement enregistré.</p> : (
              <div className="relative pl-4 space-y-3 before:absolute before:left-[4px] before:top-1.5 before:bottom-1.5 before:w-px before:bg-white/10">
                {[...active.timeline].reverse().map((ev, i) => (
                  <div key={ev.id || i} className="relative" data-testid={`tl-event-${ev.type}`}>
                    <span className="absolute -left-4 top-1 w-2.5 h-2.5 rounded-full ring-2 ring-card" style={{ background: TL_COLOR[ev.type] || "#71717a" }} />
                    <div className="text-[12px] text-zinc-200 leading-snug">{ev.label}</div>
                    <div className="text-[10px] text-zinc-500 font-mono-label">{ev.actor_name ? `${ev.actor_name} · ` : ""}{fmtDate(ev.at)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Customer history */}
          <div className="p-4">
            <p className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5 mb-2"><History size={12} /> Autres demandes ({otherTickets.length})</p>
            {otherTickets.length === 0 ? <p className="text-xs text-zinc-500">Premier ticket de ce client.</p> : (
              <div className="space-y-1.5">
                {otherTickets.slice(0, 6).map((t) => (
                  <button key={t.id} data-testid={`customer-history-${t.id}`} onClick={() => openTicket(t)} className="w-full text-left flex items-center gap-2 px-2.5 py-2 rounded-lg bg-background border border-white/10 hover:border-white/25 transition-colors">
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: TSTATUS[t.status]?.c }} />
                    <span className="text-xs truncate flex-1">{t.subject}</span>
                    <span className="font-mono-label text-[9px] text-zinc-500">{t.ref}</span>
                    <ChevronRight size={12} className="text-zinc-600 shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {["open", "answered"].includes(active.status) && (
            <div className="p-4 pt-0 mt-auto sm:hidden">
              <button data-testid="convert-ticket-button-mobile" onClick={() => setConvert({ title: active.subject, description: active.body, assigned_to: active.assigned_to || "", client_budget: 0, internal_budget: 0, member_budget: 0, deadline: "" })} className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-klein text-zinc-950 text-sm font-medium"><ListTodo size={15} /> Convertir en tâche</button>
            </div>
          )}
        </aside>
      )}
      </div>
      )}

      {/* New ticket */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-cabinet flex items-center gap-2"><Ticket size={18} className="text-klein" /> Nouveau ticket</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-400 mb-1 block">Entreprise *</label>
                <select data-testid="ticket-company" value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })} className="w-full bg-background border border-white/10 rounded-lg px-2 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20">
                  <option value="">— Choisir —</option>
                  {managed.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-400 mb-1 block">Client de l'entreprise</label>
                <select data-testid="ticket-client-select" value={selClient?.id || ""} onChange={(e) => { const ct = ticketContacts.find((c) => c.id === e.target.value); setSelClient(ct || null); if (ct) setForm({ ...form, client_email: ct.email || "" }); }} className="w-full bg-background border border-white/10 rounded-lg px-2 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20">
                  <option value="">{ticketContacts.length ? "Choisir un client…" : "Aucun client — saisir l'email"}</option>
                  {ticketContacts.map((c) => <option key={c.id} value={c.id}>{c.name}{c.email ? ` · ${c.email}` : ""}</option>)}
                </select>
                <input data-testid="ticket-client" placeholder="ou email client" value={form.client_email} onChange={(e) => { setForm({ ...form, client_email: e.target.value }); setSelClient(null); }} className="w-full bg-background border border-white/10 rounded-lg px-3 h-9 text-sm mt-2 focus:outline-none focus:ring-2 focus:ring-klein/20" />
                {selClient && (
                  <div data-testid="ticket-client-info" className="mt-2 flex items-center gap-2 text-xs text-zinc-400 bg-klein/5 border border-klein/20 rounded-lg px-2.5 py-1.5">
                    <span className="w-6 h-6 rounded-full bg-klein/15 text-klein flex items-center justify-center text-[10px] font-bold shrink-0">{(selClient.name || "?").slice(0, 1).toUpperCase()}</span>
                    <span className="truncate"><span className="text-zinc-200 font-medium">{selClient.name}</span>{selClient.phone ? ` · ${selClient.phone}` : ""}{selClient.title ? ` · ${selClient.title}` : ""}</span>
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-400 mb-1 block">Objet *</label>
              <input data-testid="ticket-subject" placeholder="Résumé de la demande" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="w-full bg-background border border-white/10 rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
            </div>
            <div>
              <label className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-400 mb-1 block">Description</label>
              <textarea data-testid="ticket-body" placeholder="Détail de la demande du client…" rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-400 mb-1.5 block">Priorité</label>
                <select data-testid="ticket-priority" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="w-full bg-background border border-white/10 rounded-lg px-2 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20">
                  {Object.entries(PRIORITY).map(([k, v]) => <option key={k} value={k}>{v.l}</option>)}
                </select>
              </div>
              <div>
                <label className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-400 mb-1.5 block">Canal d'origine</label>
                <select data-testid="ticket-source-select" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="w-full bg-background border border-white/10 rounded-lg px-2 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20">
                  {Object.entries(SOURCE).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
            </div>
          </div>
          <DialogFooter><button onClick={() => setCreating(false)} className="h-9 px-4 rounded-lg border border-white/10 text-sm font-medium hover:bg-white/[0.06]">Annuler</button><button data-testid="confirm-create-ticket" onClick={createTicket} className="h-9 px-4 rounded-lg bg-klein text-zinc-950 text-sm font-semibold hover:brightness-110">Créer le ticket</button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert ticket */}
      <Dialog open={!!convert} onOpenChange={(o) => !o && setConvert(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-cabinet flex items-center gap-2"><ListTodo size={20} className="text-klein" /> Convertir en tâche</DialogTitle></DialogHeader>
          {convert && (
            <div className="space-y-3">
              <p className="text-xs text-zinc-500">Le ticket sera fermé et une tâche créée sur le compte du client.</p>
              <input data-testid="convert-title" value={convert.title} onChange={(e) => setConvert({ ...convert, title: e.target.value })} placeholder="Titre" className="w-full border border-white/10 rounded-md px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
              <div className="grid grid-cols-2 gap-3">
                <div><label className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-400">Déléguer à</label>
                  <select data-testid="convert-assignee" value={convert.assigned_to} onChange={(e) => setConvert({ ...convert, assigned_to: e.target.value })} className="w-full border border-white/10 rounded-md px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20">
                    <option value="">— Personne —</option>
                    {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name} ({m.role})</option>)}
                  </select>
                </div>
                <div><label className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-400">Deadline</label>
                  <input type="date" value={convert.deadline} onChange={(e) => setConvert({ ...convert, deadline: e.target.value })} className="w-full border border-white/10 rounded-md px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[["client_budget", "Budget client (€)"], ["internal_budget", "Budget réalisation (€)"], ["member_budget", "Budget membre (€)"]].map(([k, l]) => (
                  <div key={k}><label className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-400">{l}</label>
                    <input data-testid={`convert-${k}`} type="number" value={convert[k]} onChange={(e) => setConvert({ ...convert, [k]: Number(e.target.value) })} className="w-full border border-white/10 rounded-md px-2 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20" />
                  </div>
                ))}
              </div>
              <div className="text-sm bg-background border border-white/10 rounded-md p-2.5 flex justify-between"><span className="text-zinc-500">Marge estimée</span><span className="font-bold text-emerald-500">{(convert.client_budget - convert.internal_budget).toFixed(0)} €</span></div>
            </div>
          )}
          <DialogFooter><button onClick={() => setConvert(null)} className="px-4 py-2 rounded-md border border-white/10 text-sm font-medium">Annuler</button><button data-testid="confirm-convert-ticket" onClick={doConvert} className="px-4 py-2 rounded-md bg-klein text-zinc-950 text-sm font-medium">Convertir & fermer</button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function TicketKanban({ tickets, onOpen, onMove, now }) {
  const onDragEnd = (r) => { if (r.destination && r.destination.droppableId !== r.source.droppableId) onMove(r.draggableId, r.destination.droppableId); };
  const STATUSES = ["open", "answered", "in_progress", "converted", "closed"];
  const bucket = (s) => (STATUSES.includes(s) ? s : (s === "auto_closed" ? "closed" : "open"));
  return (
    <div className="flex-1 overflow-x-auto p-4" data-testid="ticket-kanban">
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="flex gap-4 h-full min-w-max pb-2">
          {STATUSES.map((status) => {
            const items = tickets.filter((t) => bucket(t.status) === status);
            const c = TSTATUS[status].c;
            return (
              <Droppable droppableId={status} key={status}>
                {(provided, snapshot) => (
                  <div ref={provided.innerRef} {...provided.droppableProps} data-testid={`kanban-col-${status}`}
                    className={`w-72 shrink-0 flex flex-col rounded-xl border transition-colors ${snapshot.isDraggingOver ? "border-klein/50 bg-klein/5" : "border-white/10 bg-card"}`}>
                    <div className="px-3 py-2.5 border-b border-white/10 flex items-center gap-2 shrink-0">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: c }} />
                      <span className="font-medium text-sm">{TSTATUS[status].l}</span>
                      <span className="ml-auto text-xs font-mono-label text-zinc-500">{items.length}</span>
                    </div>
                    <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-[80px]">
                      {items.map((t, i) => (
                        <Draggable draggableId={t.id} index={i} key={t.id}>
                          {(p, snap) => (
                            <div ref={p.innerRef} {...p.draggableProps} {...p.dragHandleProps}
                              onClick={() => onOpen(t)} data-testid={`kanban-ticket-${t.id}`}
                              className={`rounded-lg border border-white/10 bg-background p-3 cursor-pointer hover:border-white/20 ${snap.isDragging ? "shadow-xl ring-1 ring-klein/40" : ""}`}
                              style={{ ...p.draggableProps.style, boxShadow: `inset 3px 0 0 ${c}` }}>
                              <div className="flex items-center gap-1.5 mb-1">
                                <PriorityDot p={t.priority} />
                                <span className="font-mono-label text-[10px] text-zinc-500">{t.ref}</span>
                                <SourceTag source={t.source} showLabel={false} />
                                {!RESOLVED.includes(t.status) && <span className={`ml-auto text-[10px] font-mono-label tabular-nums ${isPickedUp(t) ? "text-emerald-400" : "text-signal"}`}>{fmtWait(resolutionSeconds(t, now))}</span>}
                              </div>
                              <div className="text-sm font-medium leading-tight mb-1">{t.subject}</div>
                              <div className="flex items-center gap-1.5 text-[11px] text-zinc-400">
                                <span className="truncate flex-1">{t.client_name || t.client_email}</span>
                                {t.assigned_member_name && <Avatar name={t.assigned_member_name} color="#27272A" size={16} />}
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  </div>
                )}
              </Droppable>
            );
          })}
        </div>
      </DragDropContext>
    </div>
  );
}
